import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  IconButton,
  Chip,
  Box,
  Tooltip,
  Popover,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  TablePagination,
  Fab,
  Zoom,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add,
  InfoOutlined,
  ModeEditOutlineTwoTone,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Inventory2Outlined,
  LocalShippingOutlined,
  AccountCircleOutlined,
  PersonOutlineOutlined,
  EventNoteOutlined,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  format,
  addDays,
  differenceInDays,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyListDeliveryQuery } from "store/services/deliveryApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import DeliveryExcel from "./DeliveryComponents/DeliveryExcel";

const DeliveryListScreen = () => {
  const { isVendor, isDriver, vendorId, isCustomer, loggedInUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  // State for date range filter
  const [timeRange, setTimeRange] = useState("today");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const breadcrumbs = [{ title: "Deliveries" }];

  const deliveryListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    // ... (Columns remain unchanged, as provided in previous code)
    {
      name: "ID",
      selector: (row) => row.fulfillmentCompletionId,
      cell: (row) => (
        <Link
          title={row?.fulfillmentCompletionId}
          to={
            !isDriver
              ? `/app/crm/delivery/${row?._id}/detail`
              : `/app/driver/delivery/${row?._id}/detail`
          }
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.fulfillmentCompletionId ? (
            <HighlightedText
              text={row?.fulfillmentCompletionId}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    ...(!isDriver
      ? [
          {
            name: "Delivery Type",
            selector: (row) =>
              row?.responsibleUser?.name
                ? "Branch Delivery"
                : row?.sendOut?.sendOutId
                ? `Home Delivery (${row.sendOut.sendOutId})`
                : "N/A",
            cell: (row) => {
              if (row?.responsibleUser?.name) {
                return "Branch Delivery";
              }
              if (row?.sendOut?.sendOutId) {
                return !isCustomer && !isVendor && !isDriver ? (
                  <Link
                    to={`/app/crm/sendOut/${row?.sendOut?._id}/detail`}
                    className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                  >
                    <HighlightedText
                      text={`Home Delivery (${row.sendOut.sendOutId})`}
                      highlight={keyword}
                    />
                  </Link>
                ) : (
                  <HighlightedText
                    text={`Home Delivery (${row.sendOut.sendOutId})`}
                    highlight={keyword}
                  />
                );
              }
              return "N/A";
            },
          },
        ]
      : []),
    {
      name: "Shipments",
      selector: (row) => row.shipmentId,
      cell: (row) => {
        const formatShipmentId = (shipmentId) =>
          shipmentId.replace(/(.{4})/g, "$1-").slice(0, -1);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = useState(null);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedShipments, setSelectedShipments] = useState([]);

        const shipments = Array.isArray(row.shipmentId) ? row.shipmentId : [];
        const visibleShipments = shipments.slice(0, 2);
        const remainingCount = shipments.length - visibleShipments.length;

        const handleChipClick = (event, allShipments) => {
          setAnchorEl(event.currentTarget);
          setSelectedShipments(allShipments);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return shipments.length > 0 ? (
          <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
              {visibleShipments.map((shipment, index) => (
                <span key={index} style={{ fontSize: "0.9rem" }}>
                  {shipment.IsReturning
                    ? `${formatShipmentId(shipment.shipmentId)} (R)`
                    : formatShipmentId(shipment.shipmentId)}
                  {index !== visibleShipments.length - 1 && ","}
                </span>
              ))}
              {remainingCount > 0 && (
                <Tooltip title="Click to view all Shipment IDs">
                  <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    sx={{
                      marginLeft: "4px",
                      height: "18px",
                      fontSize: "10px",
                      backgroundColor: "#e0e0e0",
                      color: "#333",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleChipClick(e, shipments)}
                  />
                </Tooltip>
              )}
            </Box>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box p={2} minWidth={100}>
                {selectedShipments.map((shipment, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {shipment.IsReturning
                      ? `${formatShipmentId(shipment.shipmentId)} (R)`
                      : formatShipmentId(shipment.shipmentId)}
                  </Typography>
                ))}
              </Box>
            </Popover>
          </>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Actual Delivery Time",
      selector: (row) => <AppTableDateTime date={row?.actualDeliveryTime} />,
    },
    {
      name: "Created By",
      selector: (row) => row.createdBy?.name || "N/A",
      cell: (row) => row.createdBy?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/delivery"
          : isCustomer
          ? "/app/customer/delivery"
          : "/app/crm/delivery";
        const isEditDisabled = isVendor && row?.status !== "Pending";

        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Link
              to={
                !isDriver
                  ? `${basePath}/${row?._id}/detail`
                  : `/app/driver/delivery/${row?._id}/detail`
              }
            >
              <IconButton
                size="small"
                aria-label="View Delivery"
                color="primary"
              >
                <InfoOutlined />
              </IconButton>
            </Link>
            {!isCustomer && !isDriver && (
              <Link
                to={isEditDisabled ? "#" : `${basePath}/${row?._id}/edit`}
                style={{ pointerEvents: isEditDisabled ? "none" : "auto" }}
              >
                <IconButton
                  size="small"
                  aria-label="Edit Delivery"
                  disabled={isEditDisabled}
                  sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
                >
                  <ModeEditOutlineTwoTone />
                </IconButton>
              </Link>
            )}
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
      width: "120px",
    },
  ];

  const [fetchDeliveries, { data: deliveryRes, isLoading, isFetching, error }] =
    useLazyListDeliveryQuery();

  // Calculate date range based on selection
  const getDateRange = useCallback((range, from, to) => {
    const today = new Date();
    switch (range) {
      case "today":
        return {
          fromDate: format(startOfDay(today), "MM-dd-yyyy"),
          toDate: format(endOfDay(today), "MM-dd-yyyy"),
        };
      case "lastWeek":
        return {
          fromDate: format(
            startOfWeek(addDays(today, -7), { weekStartsOn: 1 }),
            "MM-dd-yyyy"
          ),
          toDate: format(
            endOfWeek(addDays(today, -7), { weekStartsOn: 1 }),
            "MM-dd-yyyy"
          ),
        };
      case "lastMonth":
        return {
          fromDate: format(startOfMonth(addDays(today, -30)), "MM-dd-yyyy"),
          toDate: format(endOfMonth(addDays(today, -30)), "MM-dd-yyyy"),
        };
      case "custom":
        if (from && to && new Date(from) <= new Date(to)) {
          if (differenceInDays(new Date(to), new Date(from)) > 90) {
            setErrorMessage("Date range cannot exceed 90 days.");
            return { fromDate: null, toDate: null };
          }
          return {
            fromDate: format(new Date(from), "MM-dd-yyyy"),
            toDate: format(new Date(to), "MM-dd-yyyy"),
          };
        }
        setErrorMessage(
          "Invalid date range. Ensure 'To' date is after 'From' date."
        );
        return { fromDate: null, toDate: null };
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  // Fetch deliveries with updated filters
  const fetchReportData = useCallback(() => {
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );
    const newParams = new URLSearchParams(searchParams);
    if (fromDate) newParams.set("fromDate", fromDate);
    else newParams.delete("fromDate");
    if (toDate) newParams.set("toDate", toDate);
    else newParams.delete("toDate");
    setSearchParams(newParams, { replace: true });

    fetchDeliveries({
      active: activeTab === "Active",
      page: currentPage,
      driverId: loggedInUser.typeRef,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(isVendor && { vendorId }),
      ...(fromDate && toDate && { fromDate, toDate }),
    }).catch((err) => {
      setErrorMessage(
        "Failed to fetch deliveries: " + (err?.data?.message || "Unknown error")
      );
      console.error("API Error:", err);
    });
  }, [
    activeTab,
    currentPage,
    fetchDeliveries,
    keyword,
    perPage,
    isVendor,
    vendorId,
    loggedInUser,
    timeRange,
    customFromDate,
    customToDate,
    getDateRange,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useAppMeta(`${activeTab} Deliveries`);

  const getRangeDisplay = () => {
    switch (timeRange) {
      case "today":
        return "Today";
      case "lastWeek":
        return "Last Week";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (customFromDate && customToDate) {
          return `Custom (${format(
            new Date(customFromDate),
            "MM/dd/yyyy"
          )} - ${format(new Date(customToDate), "MM/dd/yyyy")})`;
        }
        return "Custom (Invalid Dates)";
      default:
        return "All";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          {!isDriver && (
            <BaseTabs
              tabs={deliveryListTabs}
              activeTab={activeTab}
              actions={<DeliveryExcel />}
            />
          )}
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h6">
                {deliveryRes?.totalItems || 0} Deliveries - {getRangeDisplay()}{" "}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Range</InputLabel>
                  <Select
                    value={timeRange}
                    onChange={(e) => {
                      setTimeRange(e.target.value);
                      if (e.target.value !== "custom") {
                        setCustomFromDate(null);
                        setCustomToDate(null);
                        setErrorMessage("");
                      }
                    }}
                    label="Range"
                    size="small"
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                {timeRange === "custom" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DatePicker
                      label="From"
                      value={customFromDate}
                      onChange={(newValue) => {
                        setCustomFromDate(newValue);
                        if (
                          customToDate &&
                          differenceInDays(customToDate, newValue) > 90
                        ) {
                          setCustomToDate(addDays(newValue, 90));
                          setErrorMessage(
                            "Date range adjusted to maximum 90 days."
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 140 }}
                        />
                      )}
                      maxDate={customToDate || new Date()}
                      disableFuture
                    />
                    <DatePicker
                      label="To"
                      value={customToDate}
                      onChange={(newValue) => {
                        setCustomToDate(newValue);
                        if (
                          customFromDate &&
                          new Date(newValue) < new Date(customFromDate)
                        ) {
                          setErrorMessage(
                            "To date cannot be before From date."
                          );
                        } else if (
                          customFromDate &&
                          differenceInDays(newValue, customFromDate) > 90
                        ) {
                          setCustomToDate(addDays(customFromDate, 90));
                          setErrorMessage(
                            "Date range adjusted to maximum 90 days."
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 140 }}
                        />
                      )}
                      minDate={customFromDate}
                      maxDate={new Date()}
                      disableFuture
                    />
                  </Stack>
                )}

                <TableFilter
                  searchLabel="Delivery ID/Shipment ID"
                  disabled={isLoading || isFetching}
                />
              </Box>
            </Box>
            {isLoading || isFetching ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : deliveryRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No deliveries found for the selected filters. Try adjusting
                  the date range or other filters.
                </Typography>
              </Box>
            ) : isMobile ? (
              <Box sx={{ pt: 1, pb: 2 }}>
                {(deliveryRes?.data ?? []).map((row) => (
                  <DeliveryMobileCard key={row?._id} row={row} keyword={keyword} />
                ))}
                <TablePagination
                  component="div"
                  count={deliveryRes?.totalItems ?? 0}
                  page={(currentPage || 1) - 1}
                  onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
                  rowsPerPage={perPage}
                  onRowsPerPageChange={(e) =>
                    handlePerRowsChange(parseInt(e.target.value, 10))
                  }
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
                <MobileScrollArrows />
              </Box>
            ) : (
              <AppTable
                columns={columns}
                data={deliveryRes?.data ?? []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={deliveryRes?.totalItems ?? 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )}
          </Box>
          <Snackbar
            open={!!errorMessage}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default DeliveryListScreen;

const MobileScrollArrows = () => {
  const [showTopArrow, setShowTopArrow] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setShowTopArrow(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        right: 16,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        zIndex: (t) => t.zIndex.fab,
      }}
    >
      <Zoom in={showTopArrow}>
        <Fab
          size="small"
          color="primary"
          aria-label="Scroll to top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <KeyboardArrowUp />
        </Fab>
      </Zoom>
      <Fab
        size="small"
        color="primary"
        aria-label="Scroll to bottom"
        onClick={() =>
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
          })
        }
      >
        <KeyboardArrowDown />
      </Fab>
    </Box>
  );
};

const DeliveryMobileCard = ({ row, keyword }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isVendor, isCustomer, isDriver } = useAuth();

  const detailPath = !isDriver
    ? `/app/crm/delivery/${row?._id}/detail`
    : `/app/driver/delivery/${row?._id}/detail`;
  const basePath = isVendor
    ? "/app/vendor/delivery"
    : isCustomer
      ? "/app/customer/delivery"
      : "/app/crm/delivery";
  const isEditDisabled = isVendor && row?.status !== "Pending";

  const formatId = (id) => id?.replace(/(.{4})/g, "$1-").slice(0, -1);
  const shipments = Array.isArray(row?.shipmentId) ? row.shipmentId : [];
  const shipmentsText = shipments.length
    ? [
        ...shipments.slice(0, 2).map(
          (s) => `${formatId(s.shipmentId)}${s.IsReturning ? " (R)" : ""}`
        ),
        ...(shipments.length > 2 ? [`+${shipments.length - 2} more`] : []),
      ].join(", ")
    : "N/A";
  const deliveryType = row?.responsibleUser?.name
    ? "Branch Delivery"
    : row?.sendOut?.sendOutId
      ? `Home Delivery (${row.sendOut.sendOutId})`
      : "N/A";

  const renderDetailItem = (icon, label, value, iconColor = theme.palette.primary.main) => (
    <Box sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1,
          flexShrink: 0,
          backgroundColor: alpha(iconColor, 0.15),
          color: iconColor,
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ fontWeight: 500, textAlign: "right", maxWidth: "70%" }}
        >
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        mb: 1.25,
        p: 1,
        borderRadius: 0,
        backgroundColor: "action.hover",
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.selected" },
      }}
      role="button"
      tabIndex={0}
      onClick={() => navigate(detailPath)}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(detailPath);
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1,
              flexShrink: 0,
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              color: "primary.main",
            }}
          >
            <LocalShippingOutlined sx={{ fontSize: 18 }} />
          </Box>
          <Link
            title={row?.fulfillmentCompletionId}
            to={detailPath}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <Typography noWrap sx={{ fontSize: 15, fontWeight: 600 }}>
              {row?.fulfillmentCompletionId ? (
                <HighlightedText
                  text={row.fulfillmentCompletionId}
                  highlight={keyword}
                />
              ) : (
                "N/A"
              )}
            </Typography>
          </Link>
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            aria-label="View Delivery"
            color="primary"
            onClick={() => navigate(detailPath)}
          >
            <InfoOutlined sx={{ fontSize: 20 }} />
          </IconButton>
          {!isCustomer && !isDriver && (
            <IconButton
              size="small"
              aria-label="Edit Delivery"
              color="primary"
              disabled={isEditDisabled}
              onClick={() => navigate(`${basePath}/${row?._id}/edit`)}
              sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
            >
              <ModeEditOutlineTwoTone sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {renderDetailItem(
          <Inventory2Outlined sx={{ fontSize: 16 }} />,
          "Shipments",
          shipmentsText,
          theme.palette.primary.main
        )}
        {!isDriver &&
          renderDetailItem(
            <LocalShippingOutlined sx={{ fontSize: 16 }} />,
            "Delivery Type",
            deliveryType,
            theme.palette.info.main
          )}
        {renderDetailItem(
          <EventNoteOutlined sx={{ fontSize: 16 }} />,
          "Delivered On",
          <AppTableDateTime date={row?.actualDeliveryTime} />,
          theme.palette.success.main
        )}
        {renderDetailItem(
          <AccountCircleOutlined sx={{ fontSize: 16 }} />,
          "Responsible",
          row?.responsibleUser?.name,
          theme.palette.warning.main
        )}
        {renderDetailItem(
          <PersonOutlineOutlined sx={{ fontSize: 16 }} />,
          "Created By",
          row?.createdBy?.name,
          theme.palette.secondary.main
        )}
        {renderDetailItem(
          <EventNoteOutlined sx={{ fontSize: 16 }} />,
          "Created At",
          <AppTableDateTime date={row?.createdAt} />,
          theme.palette.secondary.main
        )}
      </Box>
    </Box>
  );
};

const PageActions = () => {
  const { isDriver } = useAuth();

  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        {!isDriver && (
          <Link
            to={
              isDriver
                ? "/app/driver/delivery/branch/delivery"
                : "/app/crm/delivery/branch/delivery"
            }
          >
            <Button startIcon={<Add />} variant="outlined" color="primary">
              Branch Delivery
            </Button>
          </Link>
        )}
        <Link
          to={
            isDriver
              ? "/app/driver/delivery/create"
              : "/app/crm/delivery/create"
          }
        >
          <Button startIcon={<Add />} variant="contained" color="primary">
            Door Delivery
          </Button>
        </Link>
      </div>
    </>
  );
};
